import { EventBus } from '../utils/eventBus';
import { StorageService } from '../services/storage';
import { ApiService } from '../services/api';
import { ThemeService } from '../services/theme';
import type { ImageUploadService } from '../services/imageUpload';
import { TabsService } from '../services/tabs';
import { Message, MessageContent } from '../types/chat';
import { MessageList } from './MessageList';
import { InputArea } from './InputArea';
import { ModelSelector } from './ModelSelector';
import { TabBar } from './TabBar';
import { Toolbar } from './Toolbar';
import { createElement } from '../utils/dom';

export class ChatWindow {
  private messageList: MessageList;
  private inputArea: InputArea;
  private modelSelector: ModelSelector;
  private tabBar: TabBar;
  private toolbar: Toolbar;
  private tabsService: TabsService;

  constructor(
    private eventBus: EventBus,
    private storage: StorageService,
    private api: ApiService,
    private themeService: ThemeService,
    private imageUploadService: ImageUploadService
  ) {
    this.tabsService = new TabsService(storage);

    // Initialize components
    this.messageList = new MessageList(eventBus);
    this.inputArea = new InputArea(eventBus, this.imageUploadService, api);
    this.modelSelector = new ModelSelector(eventBus, api);
    this.tabBar = new TabBar(eventBus, this.tabsService);
    this.toolbar = new Toolbar(eventBus, this.tabsService, themeService);

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    // Handle message send
    this.eventBus.on('message:send', async (data: { text: string; imageUrls: string[] }) => {
      await this.handleSendMessage(data.text, data.imageUrls);
    });

    // Handle model change
    this.eventBus.on('model:changed', (model: string) => {
      const activeConv = this.storage.getActiveConversation();
      if (activeConv) {
        this.storage.updateConversation(activeConv.id, { model });
      }
    });

    // Load active conversation when it changes
    this.eventBus.on('activeConversation:changed', (id: string) => {
      const conversation = this.storage.getConversation(id);
      if (conversation) {
        this.eventBus.emit('conversation:updated', conversation);
      }
    });
  }

  mount(container: HTMLElement): void {
    container.className = 'chat-container';

    // Header with tabs
    const header = createElement('div', 'chat-header');
    const tabBarContainer = createElement('div', 'tab-bar-container');
    this.tabBar.mount(tabBarContainer);
    header.appendChild(tabBarContainer);
    container.appendChild(header);

    // Messages area
    const messagesContainer = createElement('div', 'chat-messages');
    this.messageList.mount(messagesContainer);
    container.appendChild(messagesContainer);

    // Chat operators row (model selector + toolbar buttons)
    const operatorsRow = createElement('div', 'chat-operators-row');

    const modelSelectorContainer = createElement('div', 'model-selector-container');
    this.modelSelector.mount(modelSelectorContainer);
    operatorsRow.appendChild(modelSelectorContainer);

    const toolbarContainer = createElement('div', 'toolbar-container');
    this.toolbar.mount(toolbarContainer);
    operatorsRow.appendChild(toolbarContainer);

    container.appendChild(operatorsRow);

    // Input area
    const inputContainer = createElement('div', 'chat-input-area');
    this.inputArea.mount(inputContainer);
    container.appendChild(inputContainer);

    // Load active conversation
    const activeConv = this.storage.getActiveConversation();
    if (activeConv) {
      this.eventBus.emit('conversation:updated', activeConv);
    }
  }

  private async handleSendMessage(text: string, imageUrls: string[]): Promise<void> {
    const activeConv = this.storage.getActiveConversation();
    if (!activeConv) {
      console.error('No active conversation');
      return;
    }

    try {
      // Build user message
      let content: string | MessageContent[];

      if (imageUrls && imageUrls.length > 0) {
        // Create message with text and images
        content = [];
        if (text) {
          (content as MessageContent[]).push({ type: 'text', text });
        }
        // Add all images
        imageUrls.forEach(url => {
          (content as MessageContent[]).push({
            type: 'image_url',
            image_url: { url },
          } as MessageContent);
        });
      } else {
        content = text;
      }

      const userMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'user',
        content,
        timestamp: Date.now(),
      };

      // Add user message to storage
      this.storage.addMessage(activeConv.id, userMessage);

      // Create assistant message placeholder
      const assistantMessage: Message = {
        id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        role: 'assistant',
        content: '',
        timestamp: Date.now(),
        model: activeConv.model,
      };

      this.storage.addMessage(activeConv.id, assistantMessage);

      // Send to API and stream response
      const messages = this.storage.getMessages(activeConv.id);
      const stream = await this.api.sendMessage(messages, activeConv.model);

      let fullResponse = '';
      for await (const chunk of stream) {
        fullResponse += chunk;
        // Update message incrementally
        this.storage.updateMessage(activeConv.id, assistantMessage.id, fullResponse);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  destroy(): void {
    this.messageList.destroy();
    this.inputArea.destroy();
    this.modelSelector.destroy();
    this.tabBar.destroy();
    this.toolbar.destroy();
  }
}
